import React from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import Inventory2Icon from '@mui/icons-material/Inventory2';
import { useTranslation } from 'react-i18next';

import Seo from '../../components/SEO';
import { gearApiClient } from '../../gearApiClient';
import { gearIcons, getGearLabel } from '../../gears';
import type { GearRental, GearRentalStatus } from '../../models/GearRental';

function formatDate(value: string, language: string): string {
  try {
    return new Intl.DateTimeFormat(language, {
      dateStyle: 'medium',
    }).format(new Date(value));
  } catch {
    return value;
  }
}

function formatMoney(amount: number, currencyCode: string, language: string): string {
  try {
    return new Intl.NumberFormat(language, {
      style: 'currency',
      currency: currencyCode,
      maximumFractionDigits: 2,
    }).format(amount);
  } catch {
    return `${amount.toFixed(2)} ${currencyCode}`;
  }
}

function formatAddress(rental: GearRental): string {
  return [rental.addressLine1, rental.addressLine2, rental.city, rental.countryCode].filter(Boolean).join(', ');
}

function statusColor(status: GearRentalStatus): 'warning' | 'info' | 'success' | 'default' {
  switch (status) {
    case 'PENDING':
      return 'warning';
    case 'CONFIRMED':
      return 'info';
    case 'COMPLETED':
      return 'success';
    default:
      return 'default';
  }
}

function sortedRentals(rentals: GearRental[]): GearRental[] {
  return [...rentals].sort((left, right) => right.startDate.localeCompare(left.startDate));
}

export default function UserGearRentalsPage() {
  const { t, i18n } = useTranslation();
  const [rentals, setRentals] = React.useState<GearRental[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState('');
  const [cancelDialogOpen, setCancelDialogOpen] = React.useState(false);
  const [cancelTarget, setCancelTarget] = React.useState<GearRental | null>(null);
  const [cancelLoading, setCancelLoading] = React.useState(false);

  const loadRentals = React.useCallback(async () => {
    setLoading(true);
    setError('');

    try {
      const response = await gearApiClient.getUserGearRentals();
      setRentals(sortedRentals(response));
    } catch {
      setRentals([]);
      setError(t('userGearRentals.loadError', 'Failed to load your gear rentals'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  React.useEffect(() => {
    void loadRentals();
  }, [loadRentals]);

  const openCancelDialog = (rental: GearRental) => {
    setCancelTarget(rental);
    setCancelDialogOpen(true);
  };

  const closeCancelDialog = () => {
    if (cancelLoading) {
      return;
    }
    setCancelDialogOpen(false);
    setCancelTarget(null);
  };

  const handleCancel = async () => {
    if (!cancelTarget) {
      return;
    }

    setCancelLoading(true);
    try {
      await gearApiClient.cancelUserGearRental(cancelTarget.id);
      setRentals((previous) =>
        previous.map((rental) =>
          rental.id === cancelTarget.id ? { ...rental, status: 'CANCELLED' } : rental,
        ),
      );
      closeCancelDialog();
    } catch {
      setError(t('userGearRentals.cancelError', 'Could not cancel this rental'));
    } finally {
      setCancelLoading(false);
    }
  };

  let content: React.ReactNode = null;
  if (loading) {
    content = (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  } else if (rentals.length === 0) {
    content = (
      <Paper variant="outlined" sx={{ p: 4, borderRadius: 3, textAlign: 'center' }}>
        <Typography variant="h6" gutterBottom>
          {t('userGearRentals.emptyTitle', 'No gear rentals yet')}
        </Typography>
        <Typography color="text.secondary">
          {t('userGearRentals.emptyBody', 'Your future reservations will appear here once you make a request.')}
        </Typography>
      </Paper>
    );
  } else {
    content = (
      <Paper variant="outlined" sx={{ borderRadius: 3, overflowX: 'auto' }}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>{t('userGearRentals.columns.rental', 'Rental')}</TableCell>
                <TableCell>{t('userGearRentals.columns.dates', 'Dates')}</TableCell>
                <TableCell>{t('userGearRentals.columns.quantity', 'Quantity')}</TableCell>
                <TableCell>{t('userGearRentals.columns.status', 'Status')}</TableCell>
                <TableCell>{t('userGearRentals.columns.pickup', 'Pickup / return')}</TableCell>
                <TableCell align="right">{t('userGearRentals.columns.actions', 'Actions')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rentals.map((rental) => {
                const icon = gearIcons[rental.gearType] ?? <Inventory2Icon fontSize="small" />;
                const returnByTime = rental.returnByTime ?? '12:00';
                const showReturnNotice = rental.status === 'PENDING' || rental.status === 'CONFIRMED';

                return (
                  <TableRow key={rental.id} hover>
                    <TableCell>
                      <Stack direction="row" spacing={1.5} alignItems="center">
                        <Box
                          sx={{
                            width: 36,
                            height: 36,
                            borderRadius: '12px',
                            bgcolor: 'primary.lighter',
                            color: 'primary.main',
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}
                        >
                          {icon}
                        </Box>
                        <Box>
                          <Typography variant="body2" fontWeight={700}>
                            {getGearLabel(rental.gearType)}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {rental.organizationName}
                          </Typography>
                        </Box>
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {formatDate(rental.startDate, i18n.language)} {'\u2013'} {formatDate(rental.endDate, i18n.language)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{rental.quantity}</Typography>
                    </TableCell>
                    <TableCell>
                      <Chip size="small" color={statusColor(rental.status)} label={t(`userGearRentals.status.${rental.status}`, rental.status)} />
                    </TableCell>
                    <TableCell>
                      <Stack spacing={0.5}>
                        <Typography variant="body2">{formatAddress(rental)}</Typography>
                        {showReturnNotice ? (
                          <Typography variant="caption" color="text.secondary">
                            {t('userGearRentals.returnByNotice', 'Return by {{time}} on the return date.', { time: returnByTime })}
                          </Typography>
                        ) : null}
                      </Stack>
                    </TableCell>
                    <TableCell align="right">
                      {rental.status === 'PENDING' ? (
                        <Button color="error" variant="outlined" size="small" onClick={() => openCancelDialog(rental)}>
                          {t('userGearRentals.cancel', 'Cancel')}
                        </Button>
                      ) : null}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    );
  }

  return (
    <>
      <Seo
        title={t('userGearRentals.seoTitle', 'My gear rentals')}
        description={t('userGearRentals.seoDescription', 'Review your gear reservations and cancel pending requests.')}
      />
      <Container maxWidth="xl" sx={{ py: { xs: 3, md: 5 } }}>
        <Stack spacing={3}>
          <Box>
            <Typography component="h1" variant="h4" fontWeight={800} gutterBottom>
              {t('userGearRentals.title', 'My gear rentals')}
            </Typography>
            <Typography color="text.secondary" sx={{ maxWidth: 760 }}>
              {t('userGearRentals.subtitle', 'Track your pending and confirmed gear reservations in one place.')}
            </Typography>
          </Box>

          {error ? <Alert severity="error">{error}</Alert> : null}

          {content}
        </Stack>
      </Container>

      <Dialog open={cancelDialogOpen} onClose={closeCancelDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{t('userGearRentals.cancelDialogTitle', 'Cancel gear rental')}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <Typography>{t('userGearRentals.cancelDialogBody', 'Are you sure you want to cancel this rental?')}</Typography>
            {cancelTarget ? (
              <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, bgcolor: 'background.default' }}>
                <Stack spacing={1}>
                  <Typography variant="body2" fontWeight={700}>
                    {getGearLabel(cancelTarget.gearType)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {formatDate(cancelTarget.startDate, i18n.language)} {'\u2013'} {formatDate(cancelTarget.endDate, i18n.language)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {formatMoney(cancelTarget.pricePerDay, cancelTarget.currencyCode, i18n.language)} / {t('userGearRentals.perDay', 'day')}
                  </Typography>
                </Stack>
              </Paper>
            ) : null}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeCancelDialog} disabled={cancelLoading}>
            {t('common.cancel', 'Cancel')}
          </Button>
          <Button onClick={() => void handleCancel()} color="error" variant="contained" disabled={cancelLoading}>
            {cancelLoading ? t('common.loading', 'Loading...') : t('userGearRentals.cancel', 'Cancel')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}